// src/hooks/useSubscription.ts
// Hook personalizado para manejar el estado de suscripción del usuario

import { useState, useEffect } from 'react';
import { getSubscriptionStatus, SubscriptionStatus } from '../services/api';

export interface UseSubscriptionReturn {
  subscriptionData: SubscriptionStatus | null;
  loading: boolean;
  error: string | null;
  isActive: boolean;
  isTrial: boolean;
  isExpired: boolean;
  daysRemaining: number;
  /** Cuándo vence: el período pago si está activa, el trial si no. */
  expiresAt: Date | null;
  whatsappQuota: {
    total: number;
    used: number;
    remaining: number;
  };
  refetch: () => Promise<void>;
}

export const useSubscription = (): UseSubscriptionReturn => {
  const [subscriptionData, setSubscriptionData] = useState<SubscriptionStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchSubscription = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getSubscriptionStatus();
      setSubscriptionData(data);
    } catch (err: any) {
      console.error('Error fetching subscription:', err);
      setError(err.message || 'Error al cargar la suscripción');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSubscription();
  }, []);

  // Cuándo vence, según de qué vive la cuenta.
  //
  // Antes esto miraba SOLO trial_ends_at. Para alguien que paga, ese campo
  // quedó en el pasado (el de Florencia venció en abril), así que la cuenta
  // daba negativo y devolvía 0: el número era 0 para todos los clientes
  // pagos, siempre. Por eso nadie podía enterarse de cuándo le vencía.
  const fechaVencimiento = (): Date | null => {
    const crudo =
      subscriptionData?.status === 'active'
        ? (subscriptionData as any)?.current_period_end
        : subscriptionData?.trial_ends_at;
    if (!crudo) return null;
    const d = new Date(crudo);
    return Number.isNaN(d.getTime()) ? null : d;
  };

  const calculateDaysRemaining = (): number => {
    const vence = fechaVencimiento();
    if (!vence) return 0;
    const diffDays = Math.ceil((vence.getTime() - Date.now()) / (1000 * 60 * 60 * 24));
    return diffDays > 0 ? diffDays : 0;
  };

  // Estados derivados
  const isActive = subscriptionData?.status === 'active';
  const isTrial = subscriptionData?.status === 'trial';
  const isExpired = subscriptionData?.status === 'expired';
  const daysRemaining = calculateDaysRemaining();

  // Cuota de WhatsApp
  const whatsappQuota = {
    total: subscriptionData?.whatsapp_quota || 0,
    used: subscriptionData?.whatsapp_used || 0,
    remaining: (subscriptionData?.whatsapp_quota || 0) - (subscriptionData?.whatsapp_used || 0),
  };

  return {
    subscriptionData,
    loading,
    error,
    isActive,
    isTrial,
    isExpired,
    daysRemaining,
    expiresAt: fechaVencimiento(),
    whatsappQuota,
    refetch: fetchSubscription,
  };
};